import React, { useMemo, useCallback, useEffect, useState, memo } from 'react';
import styled from 'styled-components';

import Avatar from '../../Clients/Avatar';
import TimeFrom from '../../Common/TimeFrom';
import AttachmentList2 from '../AttachmentList2';

import { DOT_OPERATOR } from '../../../../constants/symbols';

import { usePrevious } from '../../../../hooks';
import getHtmlDocument from '../../../../utils/getHtmlDocument';

import { CheckDouble, Clock, Check, Forward } from '@icons/common';
import { ValueOf } from '@utils/tsUtils';

export const TYPES = {
  AUTO: 'auto',
  TEXT: 'text',
  IMAGE: 'image',
  VOICE: 'voice',
  DOCUMENT: 'document',
  VIDEO: 'video',
  NPS: 'nps',
  SERVICE_MENU: 'service_menu',
  SERVICE_GROUP: 'service_group',
};

type ClientType = {
  avatarUrl?: string;
  fullName?: string;
  number?: string;
  email?: string;
};

export type AttachmentType = {
  id?: string;
  name: string;
  size: number;
  url?: string;
  lastModified: number;
};

type ExtraType = {
  bcc: string;
  cc: string;
  from: string;
};

export type MessageType = {
  attachments: AttachmentType[];
  id: number | string;
  isRead: boolean;
  isAuto: boolean;
  isDelivered: boolean;
  text: string;
  time: number;
  type: ValueOf<typeof TYPES>;
  extra?: ExtraType;
  isHistory?: boolean;
  currentManagerName: string | null;
};

export interface IMessage2 {
  className?: string;
  client: ClientType;
  message: MessageType;
  style?: Record<string, string>;
  isLast?: boolean;
  isEmail?: boolean;
  rootTicketId?: string | null;
  rootChainId?: string | null;
  isClosedTicket?: boolean;
  onLoadHistoryMessages: (rootChainId: this['rootChainId']) => void;
  onRevealServiceMessages: (id: MessageType['id']) => void;
  onForwardMessage: ({
    isForwarding,
    message,
  }: {
    isForwarding: boolean;
    message: MessageType;
  }) => void;
}

const defaultClient = {
  avatarUrl: '',
  fullName: '',
  number: '',
  email: '',
};

const Message2 = ({
  message,
  client = defaultClient,
  className,
  style,
  isLast = false,
  isEmail = true,
  rootTicketId = null,
  rootChainId = null,
  onLoadHistoryMessages,
  onRevealServiceMessages,
  onForwardMessage,
  isClosedTicket,
  ...props
}: IMessage2) => {
  const { avatarUrl, fullName, number } = client;

  const {
    id,
    attachments,
    text,
    time,
    type,
    isAuto,
    isRead,
    isDelivered,
    extra,
    isHistory,
    currentManagerName,
  } = message;

  const processText = useCallback(
    stringText => {
      const doc = getHtmlDocument(stringText);
      const links = doc.getElementsByTagName('a');

      for (let i = 0; i < links.length; i += 1) {
        links[i].target = '_blank';
      }

      return doc.querySelector('body').innerHTML;
    },
    [text]
  );

  const handleLoadHistoryMessages = useCallback(() => {
    onLoadHistoryMessages(rootChainId);
  }, [rootTicketId, rootChainId, onLoadHistoryMessages]);

  const handleRevealServiceMessages = useCallback(() => {
    onRevealServiceMessages(id);
  }, [id, onRevealServiceMessages]);

  const prevText = usePrevious(text);
  const [processedText, setProcessedText] = useState(processText(text));

  const isAttachments = attachments && attachments.length;

  useEffect(() => {
    if (text !== prevText) {
      setProcessedText(processText(text));
    }
  }, [text]);

  const statusBar = useMemo(
    () => (
      <StyledStatusBar>
        {isEmail && <TimeFrom full extend time={time} />}
        {isDelivered ? (
          <StyledIsReadIcon isRead={isRead} isAuto={isAuto} />
        ) : (
          <StyledIsDeliveredIcon />
        )}
        {!isClosedTicket && isEmail && (
          <StyledForwardIcon
            onClick={() => {
              onForwardMessage({ isForwarding: true, message });
            }}
          />
        )}
        {!isEmail && <TimeFrom full extend time={time} />}
      </StyledStatusBar>
    ),
    []
  );

  if (type === TYPES.SERVICE_GROUP) {
    return (
      <StyledMessage
        isHistory={isHistory}
        className={className}
        style={style}
        {...props}
      >
        <StyledWrapper>
          <StyledExtendButton onClick={handleRevealServiceMessages}>
            Клиент пользуется меню (нажмите чтобы развернуть)
          </StyledExtendButton>
        </StyledWrapper>
      </StyledMessage>
    );
  }

  return (
    <StyledMessage
      isHistory={isHistory}
      className={className}
      style={style}
      {...props}
    >
      {isLast && rootChainId && (
        <StyledHistoryButton onClick={handleLoadHistoryMessages}>
          <StyledClockIcon />
          Показать предыдущую переписку
        </StyledHistoryButton>
      )}
      <StyledWrapper>
        <StyledAvatar name={fullName} url={avatarUrl} />
        <StyledContent>
          <StyledHeadline>
            <StyledNameWrapper>
              <StyledName title={fullName}>{fullName}</StyledName>
              {extra && extra.from && <StyledEmail>{extra.from}</StyledEmail>}
              {number && <StyledNumber>{number}</StyledNumber>}
            </StyledNameWrapper>
            {!isEmail && statusBar}
          </StyledHeadline>
          {extra && (extra.cc || extra.bcc) && (
            <StyledHeadline>
              <StyledEmail>
                Копии:
                {` ${extra.cc || ''}${
                  (extra.bcc && (extra.cc ? `,${extra.bcc}` : extra.bcc)) || ''
                }`}
              </StyledEmail>
            </StyledHeadline>
          )}
          <StyledText dangerouslySetInnerHTML={{ __html: processedText }} />
          {isAttachments && <AttachmentList2 list={attachments} />}
          {isEmail && statusBar}
          {isEmail && currentManagerName && (
            <StyledSenderName>{`Отправитель: ${currentManagerName}`}</StyledSenderName>
          )}
        </StyledContent>
      </StyledWrapper>
    </StyledMessage>
  );
};

const StyledWrapper = styled.div<{ isEmail?: boolean }>`
  padding-top: 3px;
  padding-bottom: 16px;
  display: flex;
  flex-direction: row;

  ${({ isEmail }) =>
    isEmail &&
    `
    border-top: 1px #e6e6e6 solid;
  `}
`;

const StyledMessage = styled.div<{ isHistory?: boolean }>`
  box-sizing: border-box;
  background-color: ${({ isHistory }) => (isHistory ? '#F5F3F2' : 'white')};
  white-space: revert;
  border-radius: 4px;
  padding: 0 10px;
  overflow: hidden;

  &:first-of-type {
    ${StyledWrapper} {
      border-top: none;
    }
  }
`;

const StyledAvatar = styled(Avatar)`
  height: 26px;
  width: 26px;
  min-width: 26px;
  margin-right: 8px;
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2px;
  width: 100%;

  > * + * {
    margin-top: 4px;
  }
`;

const StyledHeadline = styled.div`
  align-items: center;
  display: flex;
  width: 100%;

  > * + * {
    margin-left: 5px;
  }
`;

const StyledNameWrapper = styled.div`
  flex: 1;
`;

const StyledName = styled.div`
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledEmail = styled.span`
  color: #919499;
  font-size: 12px;
`;

const StyledNumber = styled.div`
  font-weight: 700;

  :before {
    content: '${DOT_OPERATOR}';
    margin-right: 5px;
  }
`;

const StyledText = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  color: #4d4d4d;
  line-height: 18px;
  width: 100%;
  white-space: pre-line;

  img {
    max-width: 100%;
  }

  p {
    min-height: 18px;
  }

  .forwarded {
    border: 1px solid #e1e3e1;
    width: 100%;
    border-radius: 10px;
    padding: 5px 10px;
  }

  .forwarded-info {
    font-weight: bold;
    font-style: italic;
  }
`;

const StyledExtendButton = styled.button`
  background: #f5f3f2;
  padding: 3px 13px;
  border-radius: 4px;
  width: fit-content;
  color: #1f82fe;
  cursor: pointer;
  border: none;
  outline: none;
`;

const StyledStatusBar = styled.div`
  align-items: center;
  display: flex;

  > * + * {
    margin-left: 10px;
  }
`;

const StyledIsReadIcon = styled(({ ...props }) => <CheckDouble {...props} />)`
  width: 20px;
  height: 14px;
  stroke: ${({ isRead }) => (isRead ? '#007aff' : '#979797')};

  ${({ isAuto, isRead }) =>
    isAuto &&
    !isRead &&
    `
    stroke: #007aff;
    
    path:nth-child(2) {
      stroke: #979797;
    }
  `}
`;

const StyledIsDeliveredIcon = styled(({ ...props }) => <Check {...props} />)`
  width: 14px;
  height: 8px;
  stroke: #979797;
`;

const StyledForwardIcon = styled(Forward)`
  width: 14px;
  height: 14px;
  cursor: pointer;
`;

const StyledSenderName = styled.p`
  color: #919499;
`;

const StyledHistoryButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  background-color: transparent;
  border: none;
  outline: none;
  margin-bottom: 5px;
  color: #007aff;
  cursor: pointer;
  padding: 3px 0 5px;

  span {
    margin-left: 6px;
    color: #007aff;
  }
`;

const StyledClockIcon = styled(Clock)`
  width: 16px;
  height: 16px;

  margin-right: 5px;
  path {
    stroke: #007aff;
  }
`;

export default memo(Message2);
